/**
 * LandingPage v4 — Calcutta Live-inspired redesign
 *
 * Sections (top → bottom):
 *   Hero → Showdowns → Leaderboard Row → Features → Stats → How It Works → Final CTA → Footer
 *
 * FLAGS FOR KELLY:
 *  1. "Rules" nav item links to /rules — page not yet built, placeholder route only.
 *  2. Leaderboard data requires a pre-computed 'leaderboard' subcollection under each
 *     settled game_slate. Until the settlement Cloud Function writes this, both panels
 *     show an empty state. No fake placeholder data is shown in production.
 *  3. "Stable Stars" (top purchased horses) requires cross-game aggregation — recommend
 *     a Cloud Function that maintains a top-level 'horse_stats' collection. Empty state
 *     shown until that exists.
 *  4. Stats strip numbers (20K+ members, 300+ races, 95% uptime) are aspirational
 *     placeholders copied from the Calcutta Live reference. Confirm real figures or swap
 *     for "Coming Soon" framing before launch.
 *  5. how-to-play.png bakes all explanatory text into one image — flagged as an
 *     accessibility/SEO TODO. Consider rebuilding as HTML cards in a future pass.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, orderBy, getDocs, limit,
} from 'firebase/firestore';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../services/firebase';
import { MarqueeTicker } from '../components/MarqueeTicker';
import { ShowdownCard, computePublicStatus } from '../components/ShowdownCard';
import BannerAdSlot from '../components/BannerAdSlot';
import ContactModal from '../components/ContactModal';
import LeaderboardSection from '../components/LeaderboardSection';
import HorizonSection     from '../components/HorizonSection';
import './LandingPage.css';

// ─── Age Gate ─────────────────────────────────────────────────────────────────

const AgeGate = ({ onConfirm, onDecline }) => (
  <div className="ag-backdrop">
    <div className="ag-card">
      <div className="ag-icon">18+</div>
      <h2 className="ag-heading">Adult Content</h2>
      <p className="ag-sub">You must be 18 or older to access Syndicator Showdown. Please confirm your age.</p>
      <button className="ag-btn ag-btn--confirm" onClick={onConfirm} type="button">
        I am 18 years old or older
      </button>
      <button className="ag-btn ag-btn--decline" onClick={onDecline} type="button">
        I am under 18 years old
      </button>
      <p className="ag-disclaimer">This platform involves wagering. Please gamble responsibly.</p>
    </div>
  </div>
);

// ─── Site Header (auth-aware) ─────────────────────────────────────────────────

const NAV_ITEMS = [
  { label: 'ABOUT US',     id: 'about-us',    href: '/#about-us' },
  { label: 'HOW IT WORKS', id: 'how-to-play', href: '/#how-to-play' },
  { label: 'SHOWDOWNS',    id: 'showdowns',   href: '/#showdowns' },
  { label: 'LEADERBOARD',  id: 'leaderboard', href: '/#leaderboard' },
  { label: 'LEAGUE',       id: null,          href: '/league' },
  { label: 'RULES',        id: null,          href: '/rules' },
];

// DOM order (top → bottom) so scroll-spy correctly tracks the active section
const PAGE_SECTION_ORDER = ['about-us', 'how-to-play', 'showdowns', 'leaderboard'];
const HEADER_OFFSET = 90;

const SiteHeader = () => {
  const navigate = useNavigate();
  const [currentUser,    setCurrentUser]    = useState(null);
  const [activeSection,  setActiveSection]  = useState('about-us');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Auth state
  useEffect(() => {
    return onAuthStateChanged(auth, u => setCurrentUser(u));
  }, []);

  // Scroll-spy
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      let current = 'about-us';
      for (const id of PAGE_SECTION_ORDER) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top + scrollY - HEADER_OFFSET - 24;
        if (scrollY >= top) current = id;
      }
      setActiveSection(current);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Close burger when viewport widens
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 1024) setMobileMenuOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const closeMenu = () => setMobileMenuOpen(false);

  const handleSignOut = async () => {
    try { await signOut(auth); } catch (_) {}
    navigate('/');
  };

  return (
    <header className="ss-header-container">
      <div className="ss-header-inner">

        {/* Oversized logo — absolutely positioned, links home */}
        <div className="ss-logo-wrapper">
          <Link to="/" onClick={() => { closeMenu(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <img src="/logo.png" alt="Syndicator Showdown" className="ss-logo-img" />
          </Link>
        </div>

        {/* Brand text — in-flow, also links home */}
        <Link to="/" onClick={() => { closeMenu(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="ss-brand-text">
          <h1 className="ss-brand-title">
            <span className="ss-title-1">SYNDICATOR</span>
            <span className="ss-title-2">SHOWDOWN</span>
          </h1>
          <p className="ss-brand-slogan">OWN THE DREAM. COMPETE FOR GLORY.</p>
        </Link>

        {/* Nav */}
        <nav
          id="ss-main-nav"
          className={`ss-nav-menu${mobileMenuOpen ? ' open' : ''}`}
          aria-label="Main navigation"
        >
          <ul className="ss-nav-list">
            {NAV_ITEMS.map(({ label, id, href }) => (
              <li key={label}>
                {href.startsWith('/') && !href.includes('#') ? (
                  // Pure-path routes — use React Router Link for SPA navigation
                  <Link
                    to={href}
                    className={`ss-nav-link${activeSection === id ? ' active' : ''}`}
                    onClick={closeMenu}
                  >
                    {label}
                  </Link>
                ) : (
                  // Hash links (/#section) — native <a> for reliable cross-page scroll
                  <a
                    href={href}
                    className={`ss-nav-link${activeSection === id ? ' active' : ''}`}
                    onClick={closeMenu}
                  >
                    {label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Controls — auth-aware */}
        <div className="ss-header-controls">
          {currentUser ? (
            <>
              <Link to="/dashboard" className="ss-btn-login">DASHBOARD</Link>
              <button className="ss-btn-join" onClick={handleSignOut}>SIGN OUT</button>
            </>
          ) : (
            <>
              <Link to="/signin"             className="ss-btn-login">LOGIN</Link>
              <Link to="/signin?mode=signup" className="ss-btn-join">JOIN NOW</Link>
            </>
          )}
        </div>

        {/* Hamburger toggle */}
        <button
          type="button"
          className="ss-mobile-toggle"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
          aria-controls="ss-main-nav"
          onClick={() => setMobileMenuOpen(prev => !prev)}
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>

        {/* Thin blue accent line */}
        <div className="ss-header-blue-line" aria-hidden="true" />

      </div>
    </header>
  );
};

// ─── FIX 2: Hero Section ──────────────────────────────────────────────────────

const HeroSection = () => (
  <section className="lp-hero" id="about-us">
    <div className="ss-max-container lp-hero-content">
      <div className="lp-hero-image">
        <img
          src="/winning.png"
          alt="Syndicator Showdown — Own the Dream. Compete for Glory."
        />
      </div>
      <div className="lp-hero-text">
        <h1 className="lp-hero-h1">
          FANTASY RACING GAMEPLAY.<br/>
          BASED ON LIVE AUS RACING.<br/>
          <span className="lp-hero-highlight">WILL YOUR STABLE REIGN SUPREME?</span>
        </h1>
        <p className="lp-hero-sub">
          Become a virtual racehorse owner on Australia's premium fantasy racing
          platform. Buy shares, manage your stable, and dominate the leaderboards
          for bragging rights. Be part of a community that lives for real-time racing.
        </p>
        <div className="lp-hero-cta">
          <Link to="/signin?mode=signup" className="btn btn-primary">CREATE FREE ACCOUNT</Link>
          <a href="#showdowns" className="btn btn-outline">VIEW SHOWDOWNS</a>
        </div>
      </div>
    </div>
  </section>
);

// ─── FIX 3: Upcoming Showdowns ────────────────────────────────────────────────
//
// Root-cause fix: don't filter by stored 'status' field — that field is stale
// (set during setup, e.g. 'building') and doesn't reflect the timing-derived
// live/waiting/closed state. Instead fetch ALL slates and compute status
// client-side using the same logic as the admin Games tab, then filter/sort.

const STATUS_PRIORITY = { live: 0, warning: 1, waiting: 2, building: 3 };
const PAGE_SIZE = 3;

const UpcomingShowdownsSection = () => {
  const [allSlates,    setAllSlates]    = useState([]);
  const [carouselIdx,  setCarouselIdx]  = useState(0);
  const [mobileShowAll, setMobileShowAll] = useState(false);

  useEffect(() => {
    return onSnapshot(collection(db, 'game_slates'), snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const active = docs
        .map(d => ({ ...d, _status: computePublicStatus(d) }))
        .filter(d => d._status !== 'closed');

      active.sort((a, b) => {
        const pa = STATUS_PRIORITY[a._status] ?? 99;
        const pb = STATUS_PRIORITY[b._status] ?? 99;
        if (pa !== pb) return pa - pb;
        return (a.meetingDate || '').localeCompare(b.meetingDate || '');
      });

      setAllSlates(active);
      setCarouselIdx(0);
    }, err => {
      console.error('[UpcomingShowdowns] Firestore error:', err.code, err.message);
    });
  }, []);

  const totalPages   = Math.ceil(allSlates.length / PAGE_SIZE);
  const currentPage  = Math.floor(carouselIdx / PAGE_SIZE);
  const visible      = allSlates.slice(carouselIdx, carouselIdx + PAGE_SIZE);
  const showArrows   = allSlates.length > PAGE_SIZE;
  const canGoPrev    = carouselIdx > 0;
  const canGoNext    = carouselIdx + PAGE_SIZE < allSlates.length;

  const handlePrev = () => setCarouselIdx(i => Math.max(0, i - PAGE_SIZE));
  const handleNext = () => setCarouselIdx(i => Math.min(allSlates.length - PAGE_SIZE, i + PAGE_SIZE));

  const mobileVisible = mobileShowAll ? allSlates : allSlates.slice(0, 1);
  const mobileHidden  = allSlates.length - 1;

  return (
    <section className="lp-showdowns" id="showdowns">
      <div className="ss-max-container">
        <h2 className="lp-section-title">UPCOMING SHOWDOWNS</h2>

        {/* Empty state */}
        {allSlates.length === 0 && (
          <div className="lp-showdowns-empty">
            <img src="/track-wp.png" alt="Racing track" className="lp-showdowns-empty-img" />
            <p className="lp-showdowns-empty-text">More games coming soon.</p>
          </div>
        )}

        {/* Desktop carousel */}
        {allSlates.length > 0 && (
          <>
            <div className="lp-showdown-carousel">
              {showArrows && (
                <button
                  className={`lp-carousel-arrow lp-carousel-arrow--left${!canGoPrev ? ' lp-carousel-arrow--disabled' : ''}`}
                  onClick={handlePrev}
                  disabled={!canGoPrev}
                  aria-label="Previous showdowns"
                >
                  ‹
                </button>
              )}

              <div className={`lp-showdown-grid lp-showdown-grid--count-${Math.min(visible.length, 3)}`}>
                {visible.map(showdown => (
                  <ShowdownCard key={showdown.id} showdown={showdown} />
                ))}
              </div>

              {showArrows && (
                <button
                  className={`lp-carousel-arrow lp-carousel-arrow--right${!canGoNext ? ' lp-carousel-arrow--disabled' : ''}`}
                  onClick={handleNext}
                  disabled={!canGoNext}
                  aria-label="Next showdowns"
                >
                  ›
                </button>
              )}
            </div>

            {/* Page dots */}
            {showArrows && (
              <div className="lp-carousel-dots">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    className={`lp-carousel-dot${i === currentPage ? ' lp-carousel-dot--active' : ''}`}
                    onClick={() => setCarouselIdx(i * PAGE_SIZE)}
                    aria-label={`Page ${i + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Mobile stack */}
            <div className="lp-showdown-mobile-stack">
              {mobileVisible.map(showdown => (
                <ShowdownCard key={showdown.id} showdown={showdown} />
              ))}
              {allSlates.length > 1 && !mobileShowAll && (
                <button
                  className="lp-showdown-show-more"
                  onClick={() => setMobileShowAll(true)}
                >
                  <span>Show {mobileHidden} more showdown{mobileHidden !== 1 ? 's' : ''}</span>
                  <span className="lp-showdown-show-more-arrow">↓</span>
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// ─── FIX 4: Leaderboard / Stable Stars Row ────────────────────────────────────
//
// FLAG FOR KELLY:
//   Leaderboard reads from game_slates/{id}/leaderboard subcollection.
//   This subcollection must be written by the settlement Cloud Function.
//   Until then, the panel shows an empty state.
//
//   Stable Stars reads from a top-level 'horse_stats' collection ordered by
//   purchaseCount desc. This requires a Cloud Function aggregation trigger.
//   Until then, the panel shows an empty state.

// ─── FIX 5a: Features Section ─────────────────────────────────────────────────

const FeaturesSection = () => (
  <section className="lp-features ss-max-container" id="features">
    <div className="lp-features-intro">
      <h2 className="lp-features-h2">
        BUILT FOR RACING FANS.<br/>
        BACKED BY REAL DATA.
      </h2>
      <p className="lp-features-sub">
        Trade the punt for pure strategy. Enter a SHOWDOWN, draft your stable, and prove you have what it takes to dominate the leaderboards..
      </p>
      <Link to="/signin?mode=signup" className="btn btn-primary">JOIN FOR FREE</Link>
    </div>
    <div className="lp-features-grid">
      {[
        { img: '/analytics.png', title: 'PROPRIETARY INTEL',    desc: 'Quantitative ratings & live gameplay' },
        { img: '/star.png',      title: 'TRUE ELO RANKINGS',   desc: 'Prove your skill & become a STAR' },
        { img: '/community.png', title: 'STRONG COMMUNITY',  desc: 'Connect, share & grow together' },
      ].map(f => (
        <div key={f.title} className="lp-feature">
          <img src={f.img} alt="" />
          <h4>{f.title}</h4>
          <p>{f.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

// ─── FIX 5b: Stats Strip ──────────────────────────────────────────────────────
// FLAG FOR KELLY: Numbers below are aspirational placeholders from a competitor
// reference. Confirm real figures or change to "Coming Soon" before launch.

const StatsStrip = () => (
  <section className="lp-stats-strip ss-max-container">
    {[
      { img: '/active-members.png', stat: '500+',          label: 'ACTIVE MEMBERS' },
      { img: '/cup.png',            stat: '100+',           label: 'RACES COVERED WEEKLY' },
      { img: '/uptime.png',         stat: '99%+',           label: 'MARKET UPTIME' },
      { img: '/lock.png',           stat: 'SECURE & TRUSTED', label: 'AUSTRALIAN OWNED' },
    ].map(s => (
      <div key={s.label} className="lp-stat">
        <img src={s.img} alt="" />
        <div>
          <strong>{s.stat}</strong>
          <span>{s.label}</span>
        </div>
      </div>
    ))}
  </section>
);

// ─── FIX 6: How It Works ─────────────────────────────────────────────────────
// ACCESSIBILITY TODO: how-to-play.png bakes all text (Draft/Build/Win steps)
// into a single image — screen readers won't see the content. Consider rebuilding
// as live HTML cards (icon + heading + description) in a future pass for SEO/a11y.

const HowItWorksSection = () => (
  <section className="lp-how-it-works ss-max-container" id="how-to-play">
    <img
      src="/how-to-play.png"
      alt="How It Works: Draft your stable, Build your portfolio, Win real prizes"
      className="lp-how-it-works-img"
    />
  </section>
);

// ─── FIX 7: Final CTA / Stables ──────────────────────────────────────────────

const FinalCtaSection = () => (
  <section className="lp-final-cta" id="prizes">
    <div className="ss-max-container lp-final-cta-inner">
      <div className="lp-final-cta-image">
        <img src="/stables.png" alt="" />
      </div>
      <div className="lp-final-cta-content">
        <h2 className="lp-final-cta-h2">
          READY TO BUILD A{' '}
          <span className="lp-accent-yellow">CHAMPION STABLE?</span>
        </h2>
        <p className="lp-final-cta-sub">
          Create your account today and compete for epic prizes
          at Australia's biggest race meetings.
        </p>
        <Link to="/signin?mode=signup" className="btn btn-primary">
          CREATE FREE ACCOUNT
        </Link>
        <p className="lp-final-cta-note">★ FREE TO JOIN. EASY TO PLAY.</p>
      </div>
    </div>
  </section>
);

// ─── Footer ───────────────────────────────────────────────────────────────────

const SiteFooter = ({ onContactClick }) => (
  <footer className="lp-footer">
    <div className="ss-max-container">
      <div className="lp-footer-inner">
        <Link to="/" className="lp-logo lp-logo--ft">
          <img
            src="/logo.png"
            alt="Syndicator Showdown"
            className="lp-logo-img"
            style={{ width: '40px', height: '40px' }}
            onError={e => (e.target.style.display = 'none')}
          />
          <div className="lp-logo-text">
            <span className="lp-logo-top" style={{ fontSize: '12px' }}>SYNDICATOR</span>
            <span className="lp-logo-bot" style={{ fontSize: '15px' }}>SHOWDOWN</span>
            <div className="lp-logo-deco">
              <span className="lp-deco-line" />
              <span className="lp-deco-star">✦</span>
              <span className="lp-deco-line" />
            </div>
          </div>
        </Link>
        <nav className="lp-ft-nav">
          {[
            { label: 'ABOUT US',    href: '#about-us'    },
            { label: 'HOW IT WORKS', href: '#how-to-play' },
            { label: 'SHOWDOWNS',   href: '#showdowns'   },
            { label: 'LEADERBOARD', href: '#leaderboard' },
            { label: 'LEAGUE',      href: '/league'      },
            { label: 'PRIZES',      href: '#prizes'      },
            { label: 'RULES',       href: '/rules'       },
            { label: 'PRIVACY',     href: '/privacy'     },
          ].map(({ label, href }) => (
            href.startsWith('/') ? (
              <Link key={label} to={href} className="lp-ft-lnk">{label}</Link>
            ) : (
              <a key={label} href={href} className="lp-ft-lnk">{label}</a>
            )
          ))}
          <button className="lp-ft-lnk lp-ft-lnk--btn" onClick={onContactClick}>
            CONTACT US
          </button>
        </nav>
        <div className="lp-ft-social">
          <a href="#" className="lp-socbtn" aria-label="Facebook">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
          </a>
          <a href="#" className="lp-socbtn" aria-label="Instagram">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>
          </a>
          <a href="#" className="lp-socbtn" aria-label="X">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </a>
        </div>
        <div className="lp-footer-qr">
          <img src="/qr-code.png" alt="Scan to play on mobile" className="lp-footer-qr-img" />
          <p className="lp-footer-qr-lbl">SCAN TO PLAY</p>
        </div>
      </div>
      <div className="lp-footer-bot">
        <span>© 2026 Syndicator Showdown Pty Ltd. All rights reserved. 18+ Only. Play Responsibly.</span>
        <a href="https://www.gamblinghelponline.org.au" target="_blank" rel="noopener noreferrer" className="lp-ft-help">
          "You win some, you lose more." — Gambling Help Online 1800 858 858
        </a>
      </div>
    </div>
  </footer>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
// FIX 8: Section order — Hero → Showdowns → Leaderboard → Features → Stats →
//                         How It Works → Final CTA

export const LandingPage = () => {
  const [gateVisible, setGateVisible] = useState(
    () => sessionStorage.getItem('ss_18_confirmed') !== 'yes'
  );
  const [contactOpen, setContactOpen] = useState(false);
  const [contactPrefill, setContactPrefill] = useState({});
  const [isHowToPlayModalOpen, setIsHowToPlayModalOpen] = useState(false);
  const location = useLocation();

  // Scroll to hash section when navigating from another page (e.g. /league → /#showdowns)
  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.substring(1);
      const el = document.getElementById(targetId);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  function openContact(prefill = {}) {
    setContactPrefill(prefill);
    setContactOpen(true);
  }

  return (
    <>
    <div className="lp-root">
      {gateVisible && (
        <AgeGate
          onConfirm={() => {
            sessionStorage.setItem('ss_18_confirmed', 'yes');
            setGateVisible(false);
          }}
          onDecline={() => {
            window.location.href = 'https://www.gamblinghelponline.org.au';
          }}
        />
      )}
      <SiteHeader />
      <MarqueeTicker />
      <BannerAdSlot
        onContactClick={() => openContact({ subject: 'Partnership / Sponsorship', source: 'banner' })}
      />
      <main>
        <HeroSection />
        <HowItWorksSection />
        <div className="ss-read-more-wrapper">
          <button className="lp-read-more-btn" onClick={() => setIsHowToPlayModalOpen(true)}>Read More</button>
        </div>
        <UpcomingShowdownsSection />
        <LeaderboardSection />
        <HorizonSection />
        <FeaturesSection />
        <StatsStrip />
        <FinalCtaSection />
      </main>
      <SiteFooter onContactClick={() => openContact({ subject: 'General Enquiry', source: 'footer' })} />
    </div>

    {/* Modals rendered OUTSIDE .lp-root so overflow-x:clip doesn't break position:fixed */}
    <ContactModal
      isOpen={contactOpen}
      onClose={() => setContactOpen(false)}
      prefill={contactPrefill}
    />

    {isHowToPlayModalOpen && (
      <div className="ss-modal-overlay" onClick={() => setIsHowToPlayModalOpen(false)}>
        <div className="ss-modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="ss-modal-close" onClick={() => setIsHowToPlayModalOpen(false)}>✕</button>

          <h2 className="ss-modal-title">THE AIM OF THE GAME</h2>
          <p className="ss-modal-text">Experience the thrill of racehorse ownership. Draft your ultimate stable, compete against your mates, and climb the ratings ladder by earning the most virtual prize money across a race meeting. All bankrolls and payouts are entirely virtual.</p>

          <h3 className="ss-modal-subtitle">HOW TO PLAY</h3>
          <ul className="ss-modal-list">
            <li><strong>1. Enter a Showdown:</strong> Create your free account, verify your email, and select an open Showdown from your dashboard.</li>
            <li><strong>2. Draft Your Stable:</strong> Use your allocated virtual bankroll to buy fractional shares in real racehorses.</li>
            <li><strong>3. Watch &amp; Win:</strong> As your horses race in real life, your stable earns virtual dividends based on their real-world finish (1st to 5th) and the exact equity percentage you hold.</li>
          </ul>

          <h3 className="ss-modal-subtitle">RULES OF THE DRAFT</h3>
          <ul className="ss-modal-list">
            <li><strong>Mandatory Entry &amp; Limits:</strong> You must purchase shares in at least one runner in every assigned race, and you can acquire equity in up to 50% of the field in any single race. Minimum spend limits apply.</li>
            <li><strong>Dynamic Pricing:</strong> Shares are bought in 1% increments. Draft wisely—prices dynamically increase as ownership thresholds are met.</li>
            <li><strong>Managing Your Stable:</strong> You can change your mind and trade horses before the race. However, selling a non-scratched runner incurs a 10% seller's commission.</li>
            <li><strong>Scratchings:</strong> If your horse is scratched, you can substitute it for another runner penalty-free. If you forget, automatic substitution mechanics are in place to ensure you still have a runner in the race.</li>
          </ul>
        </div>
      </div>
    )}
  </>
  );
};

export default LandingPage;
